// Gráfico de tendência a partir do banco: um gráfico só, com todas as penas, cada uma na
// sua escala (estilo supervisório — ver TrendChart). Filtros numa linha acima: regulador,
// janelas prontas, início/fim por calendário e zoom (in/out, deslocar, por área arrastando).
//
// A janela mora na URL: `janela` (pronta, termina agora e acompanha ao vivo) ou `de`/`ate`
// (fixa, em ms). A resolução vem da API pela largura da janela: 2 s até 6 h, 1 min até 7
// dias, 1 h acima — por isso o zoom BUSCA de novo em vez de só ampliar o que já veio.

import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Download, Plus, RotateCcw, Search, SquareDashedMousePointer, Table2, ZoomIn, ZoomOut } from "lucide-react";
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router";
import { formatDuration, type Pen, TrendChart } from "../components/TrendChart";
import { Badge, Button, EmptyState, PageHeader, Select, Spinner, Table, cx } from "../components/ui";
import { api, type Series, type SignalInfo, type UnitPrefs } from "../lib/api";
import { useTree } from "../lib/hooks";
import { MAX_SERIES, seriesColor } from "../lib/palette";
import { useMe } from "../lib/session";
import { displayFor, formatValue } from "../lib/units";

const RANGES: { key: string; label: string; ms: number }[] = [
  { key: "1h", label: "1 h", ms: 3600_000 },
  { key: "6h", label: "6 h", ms: 6 * 3600_000 },
  { key: "24h", label: "24 h", ms: 24 * 3600_000 },
  { key: "7d", label: "7 dias", ms: 7 * 86400_000 },
  { key: "30d", label: "30 dias", ms: 30 * 86400_000 },
];

const RESOLUTION_LABEL = {
  raw: "leituras de 2 s",
  "1m": "médias de 1 minuto",
  "1h": "médias de 1 hora",
};

/** Janela mínima do zoom: abaixo disso não há leituras (são de 2 s). */
const MIN_SPAN = 20_000;

// <input type="datetime-local"> fala hora local sem fuso: "2026-09-30T14:05".
function toLocalInput(ms: number): string {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
}
function fromLocalInput(v: string): number | null {
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
}

export function ChartsPage() {
  const { data: tree } = useTree();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const [table, setTable] = useState(false);
  const [areaZoom, setAreaZoom] = useState(false);

  const regs = useMemo(
    () =>
      tree?.groups.flatMap((g) =>
        g.sites.flatMap((s) =>
          s.units.flatMap((u) =>
            u.regulators.map((r) => ({
              id: r.id,
              label: `${s.name} › ${u.name} › ${r.name}`,
            })),
          ),
        ),
      ) ?? [],
    [tree],
  );
  const regId = Number(params.get("reg")) || regs[0]?.id || 0;
  const rangeKey = params.get("janela") ?? "1h";
  const range = RANGES.find((r) => r.key === rangeKey) ?? RANGES[0]!;
  const de = Number(params.get("de")) || null;
  const ate = Number(params.get("ate")) || null;
  const fixed = de !== null && ate !== null && ate > de ? ([de, ate] as [number, number]) : null;
  // A posição na URL é a vaga de cor de cada grandeza. Tirar uma deixa um buraco em vez de
  // puxar as outras para trás: a cor segue a grandeza, nunca a posição dela na lista.
  const slots = (params.get("g") ?? "").split(",");
  const tags = slots.filter(Boolean);
  const activeTag = tags.includes(params.get("escala") ?? "") ? params.get("escala") : (tags[0] ?? null);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) v === null ? next.delete(k) : next.set(k, v);
    setParams(next, { replace: true });
  };

  const signals = useQuery({
    queryKey: ["signals", regId],
    queryFn: () => api.get<{ signals: SignalInfo[] }>(`/api/regulators/${regId}/signals`),
    enabled: !!regId,
  });

  const live = !fixed && range.ms <= 6 * 3600_000;
  const series = useQuery({
    queryKey: ["series-live", regId, tags.join(","), fixed ? fixed.join("-") : range.key],
    queryFn: () => {
      const [from, to] = fixed ?? [Date.now() - range.ms, Date.now()];
      return api
        .get<Series>(
          `/api/regulators/${regId}/series?tags=${encodeURIComponent(tags.join(","))}&from=${new Date(from).toISOString()}&to=${new Date(to).toISOString()}`,
        )
        .then((s) => ({ ...s, xRange: [from, to] as [number, number] }));
    },
    enabled: !!regId && tags.length > 0,
    // Janela curta e pronta: acompanha ao vivo (o SSE invalida a cada leitura; isto é a rede de segurança).
    refetchInterval: live ? 15_000 : false,
    placeholderData: (prev) => prev, // mantém o gráfico enquanto recarrega: sem piscar
  });

  // A janela exibida: a fixa, ou a da última busca (termina "agora").
  const shown: [number, number] = fixed ?? series.data?.xRange ?? [Date.now() - range.ms, Date.now()];

  const colorSlot = new Map(slots.map((t, i) => [t, i] as const).filter(([t]) => t));
  const pens = useMemo<Pen[]>(
    () =>
      (series.data?.series ?? [])
        .filter((s) => tags.includes(s.tag)) // enquanto a busca nova não chega, some na hora
        .map((s) => ({
          tag: s.tag,
          description: s.description,
          color: seriesColor(colorSlot.get(s.tag) ?? 0),
          display: displayFor(s.base, s.unit, me?.unitPrefs),
          points: s.points,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series.data, me?.unitPrefs, slots.join(",")],
  );

  function setWindow(from: number, to: number) {
    const now = Date.now();
    // Não passa do agora: desloca a janela inteira para trás.
    if (to > now) {
      from -= to - now;
      to = now;
    }
    if (to - from < MIN_SPAN) {
      const c = (from + to) / 2;
      from = c - MIN_SPAN / 2;
      to = c + MIN_SPAN / 2;
    }
    update({ de: String(Math.round(from)), ate: String(Math.round(to)) });
  }
  const [from, to] = shown;
  const span = to - from;
  const zoom = (k: number) => {
    const c = (from + to) / 2;
    setWindow(c - (span * k) / 2, c + (span * k) / 2);
  };
  const pan = (k: number) => setWindow(from + span * k, to + span * k);
  const resetWindow = () => update({ de: null, ate: null });

  function addTag(tag: string) {
    if (tags.includes(tag) || tags.length >= MAX_SERIES) return;
    const next = [...slots];
    const hole = next.indexOf("");
    if (hole >= 0) next[hole] = tag;
    else next.push(tag);
    update({ g: next.join(",") });
  }
  function removeTag(tag: string) {
    const next = slots.map((t) => (t === tag ? "" : t));
    while (next.length && !next[next.length - 1]) next.pop();
    update({
      g: next.length ? next.join(",") : null,
      escala: params.get("escala") === tag ? null : params.get("escala"),
    });
  }

  const picker = <PenPicker signals={signals.data?.signals ?? []} loading={signals.isLoading} chosen={tags} prefs={me?.unitPrefs} onPick={addTag} />;

  return (
    <>
      <PageHeader title="Gráficos" description="Tendência das grandezas gravadas na nuvem." />

      {/* Filtros acima de tudo o que eles afetam. */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Select
          aria-label="Regulador"
          value={regId || ""}
          onChange={(e) => update({ reg: e.target.value, g: null, escala: null })}
          className="w-auto! min-w-56"
        >
          {regs.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </Select>
        <div className="inline-flex rounded-md border border-line bg-surface p-0.5" role="group" aria-label="Janela até agora">
          {RANGES.map((r) => {
            const on = !fixed && r.key === range.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => update({ janela: r.key, de: null, ate: null })}
                className={cx("rounded px-2.5 py-1.5 text-sm", on ? "bg-brand text-white" : "text-muted hover:bg-surface-2 hover:text-fg")}
                aria-pressed={on}
              >
                {r.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          <label className="text-muted" htmlFor="tr-de">
            De
          </label>
          <input
            id="tr-de"
            type="datetime-local"
            value={toLocalInput(from)}
            max={toLocalInput(to)}
            onChange={(e) => {
              const v = fromLocalInput(e.target.value);
              if (v !== null && v < to) setWindow(v, to);
            }}
            className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-fg [color-scheme:light_dark]"
          />
          <label className="text-muted" htmlFor="tr-ate">
            até
          </label>
          <input
            id="tr-ate"
            type="datetime-local"
            value={toLocalInput(to)}
            min={toLocalInput(from)}
            max={toLocalInput(Date.now())}
            onChange={(e) => {
              const v = fromLocalInput(e.target.value);
              if (v !== null && v > from) setWindow(from, v);
            }}
            className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-fg [color-scheme:light_dark]"
          />
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setTable(!table)} aria-pressed={table} disabled={!tags.length}>
            <Table2 className="size-3.5" aria-hidden /> {table ? "Ver gráfico" : "Ver tabela"}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => series.data && downloadCsv(series.data)} disabled={!series.data}>
            <Download className="size-3.5" aria-hidden /> CSV
          </Button>
        </div>
      </div>

      {/* Zoom: linha própria, logo abaixo da data e hora, com os nomes por extenso. */}
      <div className="mb-3 flex flex-wrap items-center gap-2" role="group" aria-label="Zoom">
        <Button size="sm" variant="secondary" onClick={() => pan(-0.5)} title="Desloca meia janela para trás">
          <ChevronLeft className="size-4" aria-hidden /> Voltar
        </Button>
        <Button size="sm" variant="secondary" onClick={() => zoom(0.5)} disabled={span <= MIN_SPAN} title="Metade da janela, no mesmo centro">
          <ZoomIn className="size-4" aria-hidden /> Aproximar
        </Button>
        <Button
          size="sm"
          variant={areaZoom ? "primary" : "secondary"}
          onClick={() => setAreaZoom(!areaZoom)}
          aria-pressed={areaZoom}
          title="Com o botão ligado, arraste sobre o gráfico para escolher o trecho a ampliar"
        >
          <SquareDashedMousePointer className="size-4" aria-hidden /> Zoom por área
        </Button>
        <Button size="sm" variant="secondary" onClick={() => zoom(2)} title="O dobro da janela, no mesmo centro">
          <ZoomOut className="size-4" aria-hidden /> Afastar
        </Button>
        <Button size="sm" variant="secondary" onClick={() => pan(0.5)} disabled={to >= Date.now() - 1000} title="Desloca meia janela para a frente">
          Avançar <ChevronRight className="size-4" aria-hidden />
        </Button>
        <Button size="sm" variant="secondary" onClick={resetWindow} disabled={!fixed} title={`Volta à janela de ${range.label} até agora`}>
          <RotateCcw className="size-4" aria-hidden /> Resetar zoom
        </Button>
        <span className="text-sm text-muted tabular-nums">janela de {formatDuration(span)}</span>
        {series.data && <Badge>{RESOLUTION_LABEL[series.data.resolution]}</Badge>}
        {live && tags.length > 0 && <Badge tone="ok">ao vivo</Badge>}
      </div>

      {!tags.length ? (
        <EmptyState title="Escolha as grandezas">
          <span className="mb-3 block">Adicione até {MAX_SERIES} penas ao gráfico.</span>
          <span className="inline-block">{picker}</span>
        </EmptyState>
      ) : series.isLoading ? (
        <Spinner />
      ) : series.error ? (
        <EmptyState title="Não foi possível carregar a série" />
      ) : table ? (
        <SeriesTable data={series.data!} prefs={me?.unitPrefs} />
      ) : (
        <div className={cx("transition-opacity", series.isPlaceholderData && "opacity-80")}>
          <TrendChart
            pens={pens}
            xRange={shown}
            aggregated={series.data!.resolution !== "raw"}
            activeTag={activeTag}
            onActivate={(t) => update({ escala: t })}
            onRemove={removeTag}
            onZoom={setWindow}
            onReset={resetWindow}
            addSlot={picker}
            areaZoom={areaZoom}
            plotHeight="max(360px, calc(100dvh - 26.5rem))"
          />
          <p className="mt-1.5 text-xs text-muted">
            Arraste as linhas A e B para medir · com “Zoom por área” ligado, arraste no gráfico para ampliar um trecho · duplo clique reseta o zoom · clique
            numa pena para ver a escala dela.
          </p>
        </div>
      )}
    </>
  );
}

/** "+ Adicionar pena": lista pesquisável das grandezas do regulador, num menu suspenso. */
function PenPicker({
  signals,
  loading,
  chosen,
  prefs,
  onPick,
}: {
  signals: SignalInfo[];
  loading: boolean;
  chosen: string[];
  prefs: UnitPrefs | undefined;
  onPick: (tag: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState<CSSProperties>({});
  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const full = chosen.length >= MAX_SERIES;

  // O menu vai num portal com posição fixa: dentro da tabela (overflow) ele ficava cortado
  // e por baixo do gráfico. Abre para baixo se couber; senão, para cima do botão.
  function toggle() {
    if (!open && root.current) {
      const r = root.current.getBoundingClientRect();
      const H = 360;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - 328));
      setPos(window.innerHeight - r.bottom >= H || r.top < H ? { left, top: r.bottom + 4 } : { left, bottom: window.innerHeight - r.top + 4 });
    }
    setOpen(!open);
  }

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent) {
        if (e.key === "Escape") setOpen(false);
        return;
      }
      const t = e.target as Node;
      if (e.type === "mousedown" && (root.current?.contains(t) || panel.current?.contains(t))) return;
      if (e.type === "scroll" && panel.current?.contains(t)) return; // rolar a própria lista
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const norm = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
  const list = signals.filter((s) => !chosen.includes(s.tag) && (!q || norm(`${s.description} ${s.tag}`).includes(norm(q))));

  return (
    <div ref={root} className="inline-block text-left">
      <button
        type="button"
        onClick={toggle}
        disabled={full}
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-brand hover:bg-surface-2 disabled:cursor-not-allowed disabled:text-muted"
      >
        <Plus className="size-3.5" aria-hidden /> {full ? `Limite de ${MAX_SERIES} penas` : `Adicionar pena (${chosen.length}/${MAX_SERIES})`}
      </button>
      {open &&
        createPortal(
          <div ref={panel} style={pos} className="fixed z-50 w-80 rounded-lg border border-line bg-surface p-2 text-left shadow-lg">
            <label className="mb-2 flex items-center gap-2 rounded-md border border-line px-2">
              <Search className="size-3.5 text-muted" aria-hidden />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar grandeza"
                className="h-8 w-full bg-transparent text-sm text-fg outline-none placeholder:text-muted"
              />
            </label>
            {loading ? (
              <Spinner />
            ) : (
              <ul className="max-h-72 overflow-y-auto">
                {list.map((s) => (
                  <li key={s.tag}>
                    <button
                      type="button"
                      onClick={() => {
                        onPick(s.tag);
                        if (chosen.length + 1 >= MAX_SERIES) setOpen(false);
                      }}
                      className="flex w-full items-baseline justify-between gap-2 rounded px-2 py-1 text-left text-sm hover:bg-surface-2"
                    >
                      <span>{s.description}</span>
                      <span className="shrink-0 text-xs text-muted">{displayFor(s.base, s.unit, prefs).unit}</span>
                    </button>
                  </li>
                ))}
                {!list.length && <li className="px-2 py-1 text-sm text-muted">Nada encontrado.</li>}
              </ul>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

/** A mesma informação do gráfico em tabela — as 500 leituras mais recentes. */
function SeriesTable({ data, prefs }: { data: Series; prefs: UnitPrefs | undefined }) {
  const rows = useMemo(() => {
    const byT = new Map<number, Map<string, number>>();
    for (const s of data.series) for (const p of s.points) (byT.get(p[0]) ?? byT.set(p[0], new Map()).get(p[0])!).set(s.tag, p[1]);
    return [...byT.entries()].sort((a, b) => b[0] - a[0]).slice(0, 500);
  }, [data]);
  return (
    <Table>
      <thead>
        <tr>
          <th>Instante</th>
          {data.series.map((s) => (
            <th key={s.tag} className="text-right">
              {s.description} ({displayFor(s.base, s.unit, prefs).unit})
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(([t, vals]) => (
          <tr key={t}>
            <td className="whitespace-nowrap text-xs text-muted tabular-nums">{new Date(t).toLocaleString("pt-BR")}</td>
            {data.series.map((s) => (
              <td key={s.tag} className="text-right tabular-nums">
                {formatValue(vals.get(s.tag), displayFor(s.base, s.unit, prefs))}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

// CSV em SI (sem prefixo), ponto e vírgula e vírgula decimal: abre direto no Excel em pt-BR.
function downloadCsv(data: Series) {
  const byT = new Map<number, Map<string, number>>();
  for (const s of data.series) for (const p of s.points) (byT.get(p[0]) ?? byT.set(p[0], new Map()).get(p[0])!).set(s.tag, p[1]);
  const header = ["instante", ...data.series.map((s) => `${s.description} (${s.unit})`)];
  const lines = [...byT.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([t, vals]) => [new Date(t).toISOString(), ...data.series.map((s) => String(vals.get(s.tag) ?? "").replace(".", ","))].join(";"));
  const blob = new Blob(["﻿" + [header.join(";"), ...lines].join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `grandezas-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
